import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

const PRIORITIES = ["Low", "Medium", "High"];
const empty = { title: "", description: "", due_date: "", priority: "Medium", status: "Open", deal_id: "", contact_id: "" };

function PageHeader({ overline, title, action }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream flex items-end justify-between">
      <div>
        <div className="overline text-gold-deep">{overline}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
        <div className="gold-rule mt-4" />
      </div>
      {action}
    </div>
  );
}

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [t, d, c] = await Promise.all([api.get("/tasks"), api.get("/deals"), api.get("/contacts")]);
    setTasks(t.data); setDeals(d.data); setContacts(c.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, deal_id: form.deal_id || null, contact_id: form.contact_id || null };
      await api.post("/tasks", payload);
      toast.success("Task created");
      setShowForm(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleDone = async (t) => {
    await api.put(`/tasks/${t.id}`, { ...t, status: t.status === "Done" ? "Open" : "Done" });
    load();
  };

  const remove = async (id) => {
    if (!(await confirmDialog({ title: "Delete this task?", description: "It will be permanently removed.", confirmLabel: "Delete", destructive: true }))) return;
    await api.delete(`/tasks/${id}`);
    load();
  };

  const open = tasks.filter((t) => t.status === "Open");
  const done = tasks.filter((t) => t.status === "Done");

  return (
    <div data-testid="tasks-page">
      <PageHeader
        overline="Follow-ups"
        title="Tasks"
        action={
          <button onClick={() => { setForm(empty); setShowForm(true); }} data-testid="tasks-add-btn" className="bg-navy text-gold px-4 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2">
            <Plus className="h-3.5 w-3.5" /> Add task
          </button>
        }
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-navy/10 p-5">
          <div className="overline text-gold-deep">Open ({open.length})</div>
          <div className="mt-4 space-y-2">
            {open.length === 0 && <div className="text-sm italic text-navy/40">All clear — no open tasks.</div>}
            {open.map((t) => (
              <div key={t.id} className="border border-navy/10 p-3 flex items-start gap-3" data-testid={`task-${t.id}`}>
                <button onClick={() => toggleDone(t)} data-testid={`task-toggle-${t.id}`} className="mt-0.5 w-5 h-5 border border-navy/30 hover:border-gold flex items-center justify-center">
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-navy">{t.title}</div>
                  {t.description && <div className="text-xs text-navy/60 mt-0.5">{t.description}</div>}
                  <div className="text-xs text-navy/50 mt-1 flex gap-3 flex-wrap">
                    {t.due_date && <span>Due {t.due_date}</span>}
                    <span className={
                      t.priority === "High" ? "text-destructive" :
                      t.priority === "Medium" ? "text-gold-deep" : "text-navy/50"
                    }>{t.priority}</span>
                  </div>
                </div>
                <button onClick={() => remove(t.id)} className="text-navy/40 hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-navy/10 p-5">
          <div className="overline text-gold-deep">Done ({done.length})</div>
          <div className="mt-4 space-y-2">
            {done.length === 0 && <div className="text-sm italic text-navy/40">Nothing here yet.</div>}
            {done.map((t) => (
              <div key={t.id} className="border border-navy/5 p-3 flex items-start gap-3 opacity-60" data-testid={`task-${t.id}`}>
                <button onClick={() => toggleDone(t)} className="mt-0.5 w-5 h-5 border border-gold bg-gold flex items-center justify-center">
                  <Check className="h-3 w-3 text-navy" />
                </button>
                <div className="flex-1 min-w-0 line-through">
                  <div className="font-medium text-navy">{t.title}</div>
                </div>
                <button onClick={() => remove(t.id)} className="text-navy/40 hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" data-testid="task-form-modal">
          <form onSubmit={save} className="bg-white max-w-xl w-full p-8">
            <div className="flex justify-between mb-6">
              <div>
                <div className="overline text-gold-deep">New</div>
                <h2 className="font-serif text-2xl text-navy">Add task</h2>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="text-navy/50">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="overline text-navy/60">Title *</label>
                <input required data-testid="task-input-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="overline text-navy/60">Description</label>
                <textarea rows={3} data-testid="task-input-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full mt-1.5 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline text-navy/60">Due date</label>
                  <input type="date" data-testid="task-input-due" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
                </div>
                <div>
                  <label className="overline text-navy/60">Priority</label>
                  <select data-testid="task-input-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold">
                    {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-navy/60">Related deal</label>
                  <select data-testid="task-input-deal" value={form.deal_id || ""} onChange={(e) => setForm({ ...form, deal_id: e.target.value })}
                    className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold">
                    <option value="">— None —</option>
                    {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-navy/60">Related contact</label>
                  <select data-testid="task-input-contact" value={form.contact_id || ""} onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                    className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold">
                    <option value="">— None —</option>
                    {contacts.map((c) => <option key={c.id} value={c.id}>{c.name} {c.organization ? `· ${c.organization}` : ""}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-navy/70">Cancel</button>
              <button type="submit" data-testid="task-form-save" className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600">
                Create
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
